/**
 * resolveNodeAccess — pure client-side node-access resolver.
 *
 * Implements SP1, SP4, SP6 from scoped-access.md:
 *   SP1: workspace role is the ceiling (EDITOR → EDIT, VIEWER/READONLY → VIEW, HELPER → NONE).
 *   SP4: effective access = strongest grant on the node or any ancestor, capped by SP1.
 *   SP6: OWNER and ADMIN always resolve to EDIT regardless of accessScope.
 *
 * Mode ALL (or missing accessScope): today's behaviour — EDITOR+ → EDIT, VIEWER/READONLY → VIEW.
 * HELPER always returns NONE (handled by SQ2 assignment rules, not grants).
 *
 * NOTE: keep in sync byte-for-byte with functions-common/src/accessScope.ts.
 * The parity test (accessScope.parity.test.ts) will catch any drift.
 */

import { ROLE_RANK } from './workspacePermissions'
import type { WorkspaceRole } from '../types/workspace'
import type { AccessScope } from '../types/schema'

export type NodeAccessResult = 'NONE' | 'VIEW' | 'EDIT'

/** Role ceiling for node access (SP1). */
function roleCeiling(role: WorkspaceRole): NodeAccessResult {
  const rank = ROLE_RANK[role]
  if (rank >= ROLE_RANK['EDITOR']) return 'EDIT'   // EDITOR, ADMIN, OWNER
  if (rank >= ROLE_RANK['READONLY']) return 'VIEW'  // VIEWER, READONLY
  return 'NONE'                                     // HELPER (rank 0)
}

/** Cap a grant result by the role ceiling (SP1). */
function capByCeiling(result: NodeAccessResult, ceiling: NodeAccessResult): NodeAccessResult {
  if (result === 'EDIT' && ceiling !== 'EDIT') return ceiling
  return result
}

/**
 * resolveNodeAccess — resolves the effective access a member has on a specific node.
 *
 * @param role      The member's workspace role.
 * @param accessScope  The member's accessScope (undefined = ALL).
 * @param ancestry  Node-first ordered list: [nodeId, parentId, …, goalId].
 *                  An empty ancestry (node not in tree) → NONE for RESTRICTED members.
 * @returns 'NONE' | 'VIEW' | 'EDIT'
 */
export function resolveNodeAccess({
  role,
  accessScope,
  ancestry,
}: {
  role: WorkspaceRole
  accessScope: AccessScope | undefined
  ancestry: string[]
}): NodeAccessResult {
  // HELPER is never resolved via grants — always NONE (SQ2 / SP2 replacement).
  if (role === 'HELPER') return 'NONE'

  // SP6: OWNER and ADMIN always get EDIT.
  if (role === 'OWNER' || role === 'ADMIN') return 'EDIT'

  const ceiling = roleCeiling(role)

  // Mode ALL or no accessScope → today's behaviour.
  const mode = accessScope?.mode ?? 'ALL'
  if (mode === 'ALL') return ceiling

  // Mode RESTRICTED: find the strongest grant on the node or any ancestor (SP4).
  const grants = accessScope?.grants ?? {}
  let best: NodeAccessResult = 'NONE'
  for (const nodeId of ancestry) {
    const raw = grants[nodeId]
    if (raw === 'EDIT') { best = 'EDIT'; break }   // short-circuit: EDIT is max
    if (raw === 'VIEW') best = 'VIEW'
  }

  // Cap by ceiling (SP1).
  return capByCeiling(best, ceiling)
}

/**
 * isRestricted — true when the member is in RESTRICTED mode.
 *
 * Returns false for OWNER and ADMIN (SP6) and for HELPER (SQ2).
 * Returns false when mode is ALL or accessScope is missing.
 */
export function isRestricted(
  role: WorkspaceRole,
  accessScope: AccessScope | undefined,
): boolean {
  if (role === 'OWNER' || role === 'ADMIN' || role === 'HELPER') return false
  return (accessScope?.mode ?? 'ALL') === 'RESTRICTED'
}
